import { useEffect } from 'react';
import gsap from 'gsap';

/**
 * Recreates the page-nav logo hover animation from the original Angular app.
 *
 * What it does on hover:
 *   • Rotates the SVG mark a full -360° (counter-clockwise spin)
 *   • Splits "Chris Boustead" into per-letter spans and gives each one a
 *     wonky rotation that fans the letters outward from a shared origin
 *
 * Reverses everything on mouse-leave so the logo settles back into place.
 *
 * The original used TweenMax/TimelineLite/Power3 (GSAP 2). The math is
 * preserved verbatim — same per-letter rotation function `l(h)` and the
 * same `rand(0.7, 1.5)` duration jitter.
 */
export function useLogoAnimation(navRef: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const root = navRef.current;
    if (!root) return;

    const wrapper = root.querySelector<HTMLElement>('.nav-logo');
    const svg = root.querySelector<SVGElement>('.nav-logo svg');
    const nameEl = root.querySelector<HTMLElement>('.logo-name');
    if (!wrapper || !svg || !nameEl) return;

    // --- Letter-wrapping -------------------------------------------------
    // Replace the bare text node with a sequence of <span>s, one per
    // glyph. Skip if already wrapped (e.g. React StrictMode double-invoke).
    const original = nameEl.dataset.originalText ?? nameEl.textContent ?? '';
    if (!nameEl.dataset.originalText) nameEl.dataset.originalText = original;
    nameEl.innerHTML = original.replace(/([^\x00-\x80]|\w)/g, '<span>$&</span>');
    nameEl.classList.add('animation-ready');

    // --- Build the GSAP timeline ----------------------------------------
    const rand = (min: number, max: number) => Math.random() * (max - min) + min;
    const ORIGIN_OFFSET = -50;
    const ARM = 45;
    const rotationFor = (distance: number) => {
      const base = Math.atan(ARM / distance) * (180 / Math.PI);
      return rand(base, 120);
    };

    // Build timeline using `.from()` so the wonky/rotated state is the
    // resting position and hover animates back to the natural readable
    // state. GSAP 3 applies `.from()` values immediately when the
    // timeline is paused (immediateRender: true is the default), which
    // matches the original Angular directive's behaviour: at rest the
    // letters of "Chris Boustead" are fanned out / unreadable, and
    // hovering the logo settles them into place.
    //
    // Easing: `power3.inOut` everywhere. Both ends of the curve are
    // softened (vs. the original's `power3.out`, which decelerates only
    // at the tail), so play *and* reverse feel like the same gentle
    // motion. Durations are stretched — 2.5s for the SVG spin and
    // 1.5–2.2s for the letters — so the whole sequence settles like a
    // slow exhale rather than a snap. The letter jitter is preserved
    // so the word still feels organic, not mechanical.
    const tl = gsap.timeline({ paused: true, defaults: { ease: 'power3.inOut' } });
    tl.from(svg, { rotation: -360, duration: 2.5 }, 0);

    const letters = nameEl.querySelectorAll<HTMLElement>('span');
    letters.forEach((letter, i) => {
      const offsetLeft = letter.offsetLeft;
      const distance = offsetLeft - ORIGIN_OFFSET;
      const direction = i % 2 === 0 ? -1 : 1;
      gsap.set(letter, { transformOrigin: `${-distance}px 50%` });
      tl.from(
        letter,
        {
          rotation: direction * rotationFor(distance),
          duration: rand(1.5, 2.2),
        },
        0.5,
      );
    });

    // --- Hover wiring ----------------------------------------------------
    const onEnter = () => tl.play();
    const onLeave = () => tl.reverse();
    wrapper.addEventListener('mouseenter', onEnter);
    wrapper.addEventListener('mouseleave', onLeave);

    return () => {
      wrapper.removeEventListener('mouseenter', onEnter);
      wrapper.removeEventListener('mouseleave', onLeave);
      tl.kill();
      // Restore the un-wrapped text so the next mount starts clean.
      nameEl.classList.remove('animation-ready');
      nameEl.textContent = original;
      delete nameEl.dataset.originalText;
    };
  }, [navRef]);
}
